import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ChevronRight, CircleCheckBig, FileCheck, Mail, ShoppingCart, UserCheck } from 'lucide-react'
import type { WorkflowSummary, WorkflowSummaryMetric } from '@/types/workflowSummary'
import { WORKFLOW_METRIC_COPY } from '@/features/order-confirmation/workflowMetricCopy'
import { SALES_ORDER_METRIC_COPY } from '@/features/sales-order/salesOrderMetricCopy'
import { salesOrderDetailQueryKeys } from '@/features/sales-order/salesOrderQueries'
import {
  getInbox,
  getSalesOrderInbox,
  getWorkflowOrderConfirmationSettings,
  getWorkflowSalesOrderSettings,
} from '@/services/api'
import { relativeTime } from '@/lib/relativeTime'
import { cn } from '@/lib/utils'

/**
 * A workflow on the front page, shown as an activated service: the product's
 * badge with a status light (active or paused), its name, how it validates
 * (automated or manual), the two counts and when the last email came in.
 * "How it runs" comes from existing endpoints for now (settings, inbox page 1);
 * once the design settles it can move into /v1/workflows/summary.
 */

const PRODUCT = {
  oc: {
    icon: FileCheck,
    base: (id: string) => `/workflow/${id}/overview`,
    inbox: (id: string) => `/workflow/${id}/inbox`,
    metricHref: (id: string, metric: string) => `/workflow/${id}/overview/${metric}`,
    copy: WORKFLOW_METRIC_COPY as Record<string, { label: string }>,
  },
  so: {
    icon: ShoppingCart,
    base: (id: string) => `/sales-order/${id}/overview`,
    inbox: (id: string) => `/sales-order/${id}/inbox`,
    metricHref: (id: string, metric: string) =>
      `/sales-order/${id}/overview/${metric === 'awaiting_validation' ? 'awaiting-validation' : metric}`,
    copy: SALES_ORDER_METRIC_COPY as Record<string, { label: string }>,
  },
} as const

function useHowItRuns(summary: WorkflowSummary) {
  const id = summary.workflow_id
  const isOc = summary.type === 'oc'
  const settings = useQuery({
    queryKey: ['service-card-settings', summary.type, id],
    queryFn: (): Promise<{ fully_automatic_validation: boolean }> =>
      isOc ? getWorkflowOrderConfirmationSettings(id) : getWorkflowSalesOrderSettings(id),
    staleTime: 5 * 60_000,
  })
  // Page 1 of the inbox, shared with the inbox page's cache: its newest email.
  const inbox = useQuery({
    queryKey: isOc ? ['inbox', id, 1] : [...salesOrderDetailQueryKeys.inbox(id), 1],
    queryFn: () => (isOc ? getInbox(id, 1) : getSalesOrderInbox(id, 1)),
    staleTime: 60_000,
  })
  const latest = inbox.data?.data?.[0]
  return {
    fullyAutomatic: settings.data?.fully_automatic_validation,
    lastEmail: latest?.received ? relativeTime(latest.received) : undefined,
    lastEmailId: latest?.id,
    inboxLoaded: inbox.isSuccess,
  }
}

function ServiceBadge({ Icon, paused }: { Icon: typeof FileCheck; paused: boolean }) {
  return (
    <span className="relative inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-white p-1 ring-1 ring-sheet-border">
      <span className="flex h-full w-full items-center justify-center rounded-full bg-gradient-to-br from-brand-600 to-brand-900 shadow-[inset_0_1px_0_rgba(255,255,255,0.25)]">
        <Icon className="h-5 w-5 text-white" strokeWidth={1.75} />
      </span>
      <span
        className={cn(
          'absolute bottom-0.5 right-0.5 h-3.5 w-3.5 rounded-full border-2 border-white',
          paused ? 'bg-amber-400' : 'bg-emerald-500',
        )}
        aria-hidden
      >
        {!paused && <span className="absolute inset-0 animate-ping rounded-full bg-emerald-500/50 motion-reduce:hidden" />}
      </span>
      <span className="sr-only">{paused ? 'Paused' : 'Active'}</span>
    </span>
  )
}

function Metric({ metric, href, label }: { metric: WorkflowSummaryMetric; href: string; label: string }) {
  return (
    <Link to={href} className="group -m-2 rounded-lg p-2 transition-colors hover:bg-white" aria-label={`${metric.count} ${label}`}>
      <p className="font-display text-4xl font-bold leading-none tracking-tight tabular-nums text-gray-900">{metric.count}</p>
      <p className="mt-2 flex items-center gap-1 text-xs font-medium text-gray-600 group-hover:text-gray-900">
        {label}
        <ChevronRight className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100" />
      </p>
    </Link>
  )
}

/** showName: there's more than one service of this product, so name the workflow too. */
export function ServiceCard({ summary, showName = false }: { summary: WorkflowSummary; showName?: boolean }) {
  const { fullyAutomatic, lastEmail, lastEmailId, inboxLoaded } = useHowItRuns(summary)
  const product = PRODUCT[summary.type]
  if (!product) return null
  const paused = summary.status === 'paused'

  return (
    <section className="rounded-panel border border-canvas-border bg-card shadow-panel">
      <Link
        to={product.base(summary.workflow_id)}
        className="group flex items-center gap-4 rounded-t-panel px-5 pb-4 pt-5"
        aria-label={`Open ${summary.product_label}`}
      >
        <ServiceBadge Icon={product.icon} paused={paused} />
        <div className="min-w-0 flex-1">
          <h2 className="font-display text-xl font-bold leading-tight tracking-tight text-gray-900">{summary.product_label}</h2>
          {showName && <p className="mt-0.5 truncate text-xs font-medium text-gray-600">{summary.workflow_name}</p>}
          {fullyAutomatic !== undefined && (
            <span className="mt-1.5 inline-flex items-center gap-1 rounded-md bg-white px-1.5 py-0.5 text-[11px] font-semibold text-gray-700 ring-1 ring-sheet-border">
              {fullyAutomatic ? <CircleCheckBig className="h-3 w-3 text-brand-600" /> : <UserCheck className="h-3 w-3 text-gray-500" />}
              {fullyAutomatic ? 'Automated validation' : 'Manual validation'}
            </span>
          )}
        </div>
        <ChevronRight className="h-5 w-5 shrink-0 text-gray-400 transition-transform group-hover:translate-x-0.5 group-hover:text-gray-700" />
      </Link>

      <div className="mx-5 grid grid-cols-2 gap-4 border-t border-sheet-border py-5">
        {summary.metrics.map((metric) => {
          const label = product.copy[metric.id]?.label ?? metric.label
          return <Metric key={metric.id} metric={metric} href={product.metricHref(summary.workflow_id, metric.id)} label={label} />
        })}
      </div>

      {inboxLoaded && (
        <Link
          to={lastEmailId ? `${product.inbox(summary.workflow_id)}/${lastEmailId}` : product.inbox(summary.workflow_id)}
          className="group flex items-center gap-1.5 rounded-b-panel border-t border-sheet-border bg-white/60 px-5 py-2.5 text-xs text-gray-600 transition-colors hover:bg-white hover:text-gray-900"
        >
          <Mail className="h-3.5 w-3.5 text-gray-400 group-hover:text-gray-700" />
          <span className="decoration-gray-400 underline-offset-2 group-hover:underline">
            {lastEmail ? `Last email ${lastEmail}` : 'No emails yet'}
          </span>
          <ChevronRight className="ml-auto h-3.5 w-3.5 opacity-0 transition-opacity group-hover:opacity-100" />
        </Link>
      )}
    </section>
  )
}
